import React, {useEffect, useRef, useState} from "react";
import {gsap} from "gsap";
import "../utils.css";
import pic from "../assets/pic.png";
import Modal from "./Modal";
import HireMeForm from "./HireMeForm";
import Skills from "./Skills";

const Hero = ({setToast}) => {
  const swingRefs = useRef([]);
  const hireMeRef = useRef(null);
  const handWaveRef = useRef(null);

  const modalOverlayRef = useRef(null);
  const modalContentRef = useRef(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const swing = (target) => {
    if (!target) return;

    const vars = {
      target,
      origin: "top center",
      perspective: 600,
      ease: "power1.inOut",
      power: 1,
      duration: 2,
      rotation: -90,
      maxrotation: 60,
      speed: 0.3,
      maxspeed: 0.1,
    };

    gsap.set(vars.target, {
      rotationX: vars.rotation,
      transformOrigin: vars.origin,
      transformPerspective: vars.perspective,
    });

    gsap.to(vars, vars.duration, {
      power: 0,
      delay: 0.5,
      onStart: nextSwing,
      onStartParams: [vars],
    });
  };

  const nextSwing = (vars) => {
    if (vars.power > 0) {
      vars.rotation = (vars.rotation > 0 ? -1 : 1) * vars.maxrotation * vars.power;
      gsap.to(vars.target, vars.speed + vars.maxspeed * vars.power, {
        rotationX: vars.rotation,
        ease: vars.ease,
        onComplete: nextSwing,
        onCompleteParams: [vars],
      });
    } else {
      gsap.to(vars.target, vars.speed, {
        rotationX: 0,
        ease: vars.ease,
        clearProps: "all",
      });
    }
  };

  useEffect(() => {
    swingRefs.current.forEach((ref) => {
      if (ref) swing(ref);
    });

    if (handWaveRef.current) {
      gsap.fromTo(
        handWaveRef.current,
        {rotation: 0},
        {
          rotation: 30,
          duration: 1,
          repeat: -1,
          yoyo: true,
          ease: "power1.inOut",
        }
      );
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const handleMouseMove = (e) => {
      const button = hireMeRef.current;
      if (!button) return;

      const rect = button.getBoundingClientRect();
      const distanceX = e.clientX - (rect.left + rect.width / 2);
      const distanceY = e.clientY - (rect.top + rect.height / 2);
      const distance = Math.sqrt(distanceX ** 2 + distanceY ** 2);

      const maxDistance = 150;
      if (distance < maxDistance) {
        gsap.to(button, {
          x: distanceX * 0.2,
          y: distanceY * 0.2,
          duration: 0.3,
          ease: "power2.out",
        });
      } else {
        gsap.to(button, {
          x: 0,
          y: 0,
          duration: 0.3,
          ease: "power2.out",
        });
      }
    };

    document.addEventListener("mousemove", handleMouseMove);
    return () => document.removeEventListener("mousemove", handleMouseMove);
  }, []);

  const openModal = () => {
    const button = hireMeRef.current;
    const modalOverlay = modalOverlayRef.current;
    const modalContent = modalContentRef.current;

    if (!button || !modalOverlay || !modalContent) return;

    const rect = button.getBoundingClientRect();

    gsap.set(modalOverlay, {
      display: "block",
      left: rect.left,
      top: rect.top,
      width: rect.width,
      height: rect.height,
      opacity: 1,
      boxShadow: "5px 5px 0px 5px black",
    });

    gsap.set(modalContent, {
      opacity: 0,
      height: 0,
      width: 0,
    });

    gsap.to(modalOverlay, {
      left: "50%",
      top: "50%",
      xPercent: -50,
      yPercent: -50,
      width: "80vw",
      height: "80vh",
      duration: 0.5,
      ease: "power2.inOut",
      onComplete: () => {
        gsap.to(modalContent, {
          opacity: 1,
          duration: 0.3,
          height: "100%",
          width: "100%",
        });
      },
    });

    setIsModalOpen(true);
    document.body.style.overflow = "hidden";
  };

  const closeModal = () => {
    const button = hireMeRef.current;
    const modalOverlay = modalOverlayRef.current;
    const modalContent = modalContentRef.current;

    if (!button || !modalOverlay || !modalContent) return;

    const rect = button.getBoundingClientRect();

    gsap.to(modalContent, {
      opacity: 0,
      duration: 0.3,
      onComplete: () => {
        gsap.to(modalOverlay, {
          left: rect.left,
          top: rect.top,
          xPercent: 0,
          yPercent: 0,
          width: rect.width,
          height: rect.height,
          duration: 0.5,
          ease: "power2.inOut",
          onComplete: () => {
            gsap.set(modalOverlay, {display: "none"});
            setIsModalOpen(false);
          },
        });
      },
    });

    document.body.style.overflow = "auto";
  };

  const setSwingRef = (index) => (el) => {
    swingRefs.current[index] = el;
  };

  return (
    <main className="bg-vector-img bg-x-gray-dark text-white">
      <div className="md:mx-24 mx-5 pt-16 flex md:flex-row flex-col-reverse items-center justify-center">
        {/* Introduction section */}
        <div className="about w-full text-center md:text-left">
          <div className="text-2xl mt-3 md:mt-0 md:text-3xl">
            <p className="inline-block" ref={handWaveRef}>
              👋
            </p>
            Hello! I am
          </div>
          <h1 className="altamas text-6xl md:text-8xl text-x-yellow whitespace-nowrap">Altamas Shaikh</h1>

          {/* Description with animated elements */}
          <div>
            <span className="md:text-xl text-xl">
              Highly
              <div ref={setSwingRef(0)} className="bg-x-green inline-block text-black px-1 rounded mx-1">
                Motivated
              </div>
              and
              <div ref={setSwingRef(1)} className="bg-x-cyan inline-block text-black px-1 rounded mx-1 whitespace-nowrap">
                Detail-oriented
              </div>
              <div ref={setSwingRef(2)} className="bg-x-yellow inline-block text-black px-1 rounded whitespace-nowrap">
                Backend developer
              </div>
              &nbsp;having 1 year of hands-on experience, specializing in Node.js and modern backend technologies. I have a strong foundation in building scalable, secure, and high-performance server-side applications, with a proven ability to write clean, maintainable code and debug complex issues.
              <br />
              <br />
              Over the past year, I have contributed to real-world projects involving
              <div ref={setSwingRef(3)} className="bg-x-cyan inline-block text-black px-1 rounded mx-1">
                RESTful APIs,
              </div>
              <div ref={setSwingRef(4)} className="bg-x-orange inline-block text-black px-1 rounded mx-1 whitespace-nowrap">
                database design (SQL & NoSQL)
              </div>
              <div ref={setSwingRef(5)} className="bg-x-green inline-block text-black px-1 rounded whitespace-nowrap">
                authentication systems
              </div>
              and
              <div ref={setSwingRef(6)} className="bg-x-yellow inline-block text-black px-1 rounded mx-1">
                third-party integrations.
              </div>
              My adaptability and quick grasp of new codebases enable me to contribute efficiently from day one. I am passionate about solving real problems with code and continuously improving both my technical and soft skills.
            </span>
          </div>

          <button ref={hireMeRef} className="bg-x-cyan text-2xl rounded-full text-x-gray-dark font-bold h-14 w-48 mt-5" style={{boxShadow: "5px 5px 0px 5px black"}} disabled={isModalOpen} onClick={openModal}>
            HIRE ME
          </button>

          {/* Modal */}
          <Modal color="bg-x-cyan" isModalOpen={isModalOpen} closeModal={closeModal} modalOverlayRef={modalOverlayRef} modalContentRef={modalContentRef}>
            <HireMeForm setToast={setToast} closeModal={closeModal} />
          </Modal>
        </div>

        {/* Profile picture */}
        <div className="flex justify-center md:justify-end mt-10 md:ms-40">
          <div className="bg-x-yellow h-72 w-72 rounded-[50px] relative" style={{boxShadow: "5px 5px 0px 5px black"}}>
            <img src={pic} className="absolute -top-[60px] rounded-[50px]" alt="Profile" />
          </div>
        </div>
      </div>

      {/* Skills section */}
      <Skills />
    </main>
  );
};

export default Hero;
